import { FC, useState, useRef, useEffect } from "react";
import clsx from "classnames";
import { useStyles } from "./NotchItem.style";
import { Image } from "@onmo/ui";
import { assetsImages } from "../../../models/theme";

interface IArrowCollapseProps {
  onClose?: () => void;
  open?: boolean;
}

export const NotchItem: FC<IArrowCollapseProps> = (props) => {
  const classes = useStyles();
  const { onClose, open } = props;

  const animationFrameRef = useRef(0);
  const [position, setPosition] = useState<"left" | "right">("left");

  useEffect(() => {
    const dragElement = (elmnt: HTMLDivElement) => {
      let pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;

      const getClientXY = (e: MouseEvent | TouchEvent) => {
        if ("touches" in e) {
          return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        } else {
          return { x: e.clientX, y: e.clientY };
        }
      };

      const dragStart = (e: MouseEvent | TouchEvent) => {
        e.preventDefault();
        const { x, y } = getClientXY(e);
        pos3 = x;
        pos4 = y;

        document.onmouseup = dragEnd;
        document.onmousemove = dragging;
        document.ontouchend = dragEnd;
        document.ontouchmove = dragging;
      };

      const dragging = (e: MouseEvent | TouchEvent) => {
        const { x, y } = getClientXY(e);
        pos1 = pos3 - x;
        pos2 = pos4 - y;
        pos3 = x;
        pos4 = y;

        elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
      };

      const dragEnd = (e: MouseEvent | TouchEvent) => {
        document.onmouseup = null;
        document.onmousemove = null;
        document.ontouchend = null;
        document.ontouchmove = null;

        const screenWidth = window.innerWidth;
        const moveTo =
          elmnt.offsetLeft < screenWidth / 2
            ? -12
            : screenWidth - elmnt.offsetWidth + 12;

        setPosition(elmnt.offsetLeft < screenWidth / 2 ? "left" : "right");

        if (elmnt.offsetLeft === moveTo) {
          onClose?.();
        }

        const smoothMove = () => {
          const currentLeft = elmnt.offsetLeft;
          const distance = moveTo - currentLeft;
          const speed = distance * 0.2;

          if (elmnt.offsetTop < 30) {
            elmnt.style.top = `30px`;
          }
          if (elmnt.offsetTop > window.innerHeight - 50) {
            elmnt.style.top = `${window.innerHeight - 50}px`;
          }

          if (Math.abs(distance) > 2) {
            elmnt.style.left = `${currentLeft + speed}px`;
            animationFrameRef.current = requestAnimationFrame(smoothMove);
          } else {
            elmnt.style.left = `${moveTo}px`;
            cancelAnimationFrame(animationFrameRef.current);
          }
        };

        animationFrameRef.current = requestAnimationFrame(smoothMove);
      };

      elmnt.ontouchstart = dragStart;
      elmnt.onmousedown = dragStart;
    };

    const elmnt = document.getElementById("notch-logo-btn");
    if (elmnt) {
      dragElement(elmnt as HTMLDivElement);
    }

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [onClose]);

  useEffect(() => {
    const handleResize = () => {
      const elmnt = document.getElementById("notch-logo-btn") as HTMLDivElement;

      if (!elmnt) return;
      
      const top = Math.min(
        Math.max(elmnt.offsetTop, 30),
        window.innerHeight - 50,
      );
      elmnt.style.top = `${top}px`;

      elmnt.style.left =
        position === "left"
          ? "-12px"
          : `${window.innerWidth - elmnt.offsetWidth + 12}px`;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [position]);

  // Clean up animation frames
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  return (
    <div
      id="notch-logo-btn"
      className={clsx(classes.logoIcon, position, { open })}
    >
      <Image src={assetsImages.notchBadgeLogo} width={50} height={50} />
    </div>
  );
};
